import React, { Fragment, useState } from "react";
import { createReactTableColumnHelper } from "../../../components/src/react-table/features";
import { gql, useQuery, useMutation } from "urql";
import { useParams, Link, useLocation } from "wouter";
import { sortBy } from "lodash";
import { Button } from "../../../components/src/Button";
import { cloneDeep } from "lodash";
import { Ban, Check, RotateCcw, TriangleAlert } from "lucide-react";

import { TooltipLabel } from "../../../components/src/TooltipLabel";
import { useQueryState } from "../../../components/src/use-query-state";
import { Panel } from "../../../components/src/common/Panel";
import { twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { YesNoModal } from "../../../components/src/YesNoModal";
import { dateFormater, serializeSwedenDate } from "../../../components/src/dateFormater";

import { MiniTransactionForm, miniTransactionTypes } from "./MiniTransactionForm";
import { CorporateActionForm } from "./CorporateActionForm";
import {
    CorporateAction,
    CorporateActionEvent,
    CorporateActionInput,
    CorporateActionStatusEnum,
    InstrumentModelTypeEnum,
    MatchCoacsTransactionsInput,
    MatchingCoacsStatus,
    MiniTransaction,
    PermissionAssetEnum,
    TransactionStatus,
    TransactionType,
    UpdateTransactionInput
} from "../types.generated";
import { GET_ME } from "../common/queries";
import { ICommandBarMenuItem } from "../../../components/src/common/CommandBar";
import { ReactDataGrid } from "../../../components/src/ReactDataGrid";


const GET_CORPORATE_ACTIONS = gql`
    query corporateActions($filter: CorporateActionFilterInput) {
        corporateActions(filter: $filter) {
            _id
            clientId
            client {
                _id
                name
            }
            recordDate
            exDate
            paymentDate
            type
            instrument {
                _id
                name
                isin
            }
            currency
            amount
            foreignTax
            messageType
            corporateActionEvent
            status
            text
            externalId
            error
            correspondingTransactionId
            correspondingTransaction {
                _id
                status
            }
            updateTimestamp
            reportIds
            swiftId
            name
        }
    }
`;
const GET_MINI_TRANSACTIONS = gql`
    query miniTransactions($status: [TransactionStatus], $type: [TransactionType]) {
        miniTransactions(status: $status, type: $type) {
            _id
            type
            accountId
            tradeDate
            effectiveDate
            description
            status
            error
            bestExecutionType
            brokerId
            broker {
                _id
                name
            }
            uniqueTradeId
            brokerTradeId
            source
            instrumentId
            instrument {
                _id
                name
                modelType
            }
            currency
            valueDate
            settlementAmount
            clientId
            client {
                _id
                name
            }
            externalId
            quantity
            price
            amount
            commission
            stampDuty
            foreignTax
            accruedInterest
            agreementType
            clearingHouseId
            secondInstrumentId
            secondAmount
            secondCurrency
            bonusShares
            fee
            itemType
            updateTimestamp
        }
    }
`;

const MATCH_COACS_TRANSACTIONS = gql`
    mutation MatchCoacsTransactions($input: [MatchCoacsTransactionsInput!]!) {
        matchCoacsTransactions(input: $input) {
            corporateAction {
                _id
                status
                error
                correspondingTransactionId
            }
            transaction {
                _id
                status
                error
                brokerTradeId
            }
        }
    }
`;

const UPSERT_CORPORATE_ACTIONS = gql`
    mutation upsertCorporateActions($input: [CorporateActionInput!]!) {
        upsertCorporateActions(input: $input) {
            _id
            clientId
            status
        }
    }
`;

const UPDATE_TRANSACTIONS = gql`
    mutation updateTransactions($input: [UpdateTransactionInput!]!) {
        updateTransactions(input: $input) {
            _id
        }
    }
`;

type TransactionCorporateAction = CorporateActionExtended | MiniTransactionExtended;

type CorporateActionExtended = CorporateAction & {
    clientName: string;
    instrumentName: string;
    valueDate: string;
    tradeDate: string;
};

type MiniTransactionExtended = MiniTransaction & {
    clientName: string;
    instrumentName: string;
};

const transactionCoacsGroups: string[] = ["Pending - NOT MATCHED", "Pending - MATCHED", "Preliminary", "Confirmed"];

const groupByStatus = (transactionsCoacs: (MiniTransaction | CorporateAction)[], transactionCoacsGroups: string[]) => {
    const result: Record<string, (MiniTransaction | CorporateAction)[]> = {};
    transactionCoacsGroups.forEach((status) => {
        result[status] = [];
    });

    transactionsCoacs = sortBy(transactionsCoacs, "valueDate");
    transactionsCoacs.forEach((transactionCoacs) => {
        let status = transactionCoacs.status as string;

        if (status === "Pending") {
            if ((transactionCoacs as CorporateAction).correspondingTransactionId || (transactionCoacs as MiniTransaction).brokerTradeId) {
                status = "Pending - MATCHED";
            } else {
                status = "Pending - NOT MATCHED";
            }
        }

        if (transactionCoacsGroups.includes(status)) {
            result[status].push(transactionCoacs);
        }
    });

    return result;
};

export const CorporateActionsUpdateGrid = ({ items, onUpdateStatusClick, onMatchTransactionClick }): React.ReactElement => {
    const [selected, setSelected] = useState(null);
    const [modal, setModal] = useState({ showModal: false, payload: null });

    const columnHelper = createReactTableColumnHelper<any>();

    const columns = [
        columnHelper.accessor("clientName", {
            header: "Client",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return cellProps.row.original.client && cellProps.row.original.client._id ? (
                    <Link to={`/parties/${cellProps.row.original.client._id}`}>{value}</Link>
                ) : null;
            }
        }),
        columnHelper.accessor("externalId", {
            header: "Event ref/external id",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (cellProps.row.original.__typename === "MiniTransaction") {
                    return miniTransactionTypes.find((d) => d === cellProps.row.original.type) ? (
                        <Link to={"/reconcile/corporateactions/minitransaction/" + cellProps.row.original._id}>{value}</Link>
                    ) : null;
                } else {
                    return <Link to={"/reconcile/corporateactions/corporateaction/" + cellProps.row.original._id}>{value}</Link>;
                }
            }
        }),
        columnHelper.accessor("status", {
            header: "Status",
            cell: (cellProps) => {
                return cellProps.getValue();
            }
        }),
        columnHelper.accessor("type", {
            header: "Type",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (cellProps.row.original.__typename === "MiniTransaction") {
                    return miniTransactionTypes.find((d) => d === value) ? (
                        <Link to={"/reconcile/corporateactions/minitransaction/" + cellProps.row.original._id}>{value}</Link>
                    ) : (
                        <span>{value}</span>
                    );
                } else {
                    return (
                        <Link to={"/reconcile/corporateactions/corporateaction/" + cellProps.row.original._id}>
                            {cellProps.row.original.corporateActionEvent}
                        </Link>
                    );
                }
            }
        }),
        columnHelper.accessor("instrumentName", {
            header: "Instrument",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return cellProps.row.original.instrument && cellProps.row.original.instrument._id ? (
                    <Link key={value} to={`/instruments/${cellProps.row.original.instrument._id}`}>
                        {value}
                    </Link>
                ) : (
                    <span>{value}</span>
                );
            }
        }),
        columnHelper.accessor("foreignTax", {
            header: "Foreign tax",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return twoDecPriceFormat(value);
            }
        }),
        columnHelper.accessor("amount", {
            header: "Amount",
            cell: (cellProps) => {
                return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>;
            }
        }),
        columnHelper.accessor("currency", {
            header: "Currency",
            cell: (cellProps) => {
                return cellProps.getValue();
            }
        }),
        columnHelper.accessor("tradeDate", {
            header: "Trade date",
            cell: (cellProps) => {
                return cellProps.getValue();
            }
        }),
        columnHelper.accessor("valueDate", {
            header: "Value date",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (value < new Date().toISOString().slice(0, 10) && cellProps.row.original.status !== "Settled") {
                    return (
                        <span
                            style={{
                                color: "white",
                                backgroundColor: "crimson"
                            }}
                        >
                            {value}
                        </span>
                    );
                } else {
                    return <span>{value}</span>;
                }
            }
        }),
        columnHelper.accessor("error", {
            header: "Error",
            cell: (cellProps) => {
                return cellProps.getValue();
            }
        }),
        columnHelper.accessor("correspondingTransactionId", {
            header: "Matched with",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (value) {
                    return <Link to={`/reconcile/corporateactions/minitransaction/${value}`}>{value}</Link>;
                } else {
                    return cellProps.row.original.brokerTradeId ? cellProps.row.original.brokerTradeId : null;
                }
            }
        }),
        columnHelper.accessor("updateTimestamp", {
            header: "Update timestamp",
            cell: (cellProps) => {
                return dateFormater(cellProps.getValue());
            }
        })
    ];

    const commands: ICommandBarMenuItem[] = [
        {
            itemKey: "newItem",
            text: "Change selection status",
            subMenuProps: {
                items: [
                    {
                        itemKey: "pending",
                        text: "Change to Pending",
                        icon: <RotateCcw />,
                        onClick: () => {
                            if (onUpdateStatusClick) {
                                onUpdateStatusClick(selected, "Pending");
                            }
                        }
                    },
                    {
                        itemKey: "preliminary",
                        text: "Change to Preliminary",
                        icon: <TriangleAlert />,
                        onClick: () => {
                            if (onUpdateStatusClick) {
                                onUpdateStatusClick(selected, "Preliminary");
                            }
                        }
                    },
                    {
                        itemKey: "confirmed",
                        text: "Change to Confirmed",
                        icon: <Check />,
                        onClick: () => {
                            if (onUpdateStatusClick) {
                                onUpdateStatusClick(selected, "Confirmed");
                            }
                        }
                    },
                    {
                        itemKey: "settled",
                        text: "Change to Settled",
                        onClick: () => {
                            if (onUpdateStatusClick) {
                                onUpdateStatusClick(selected, "Settled");
                            }
                        }
                    },
                    {
                        itemKey: "deleted",
                        text: "Change to Deleted",
                        icon: <Ban />,
                        onClick: () => {
                            setModal({
                                showModal: true,
                                payload: { newStatus: "Deleted", ids: selected }
                            });
                        }
                    }
                ]
            }
        }
    ];

    // Match Transaction commands
    if (selected && selected.length === 1) {
        if (selected[0].correspondingTransactionId) {
            const removeMatchMenuItem = {
                itemKey: "removeMatchMenuItem",
                text: "Remove Match",
                onClick: () => {
                    if (onMatchTransactionClick) {
                        onMatchTransactionClick(selected[0], "remove");
                    }
                }
            };
            commands.push(removeMatchMenuItem);
        } else if (
            selected[0].__typename === "CorporateAction" &&
            selected[0].status === CorporateActionStatusEnum.Pending &&
            !selected[0].correspondingTransactionId
        ) {
            const noMatchManuallyOkMenuItem = {
                itemKey: "noMatchManuallyOkMenuItem",
                text: "No match, manually checked",
                onClick: () => {
                    if (onMatchTransactionClick) {
                        onMatchTransactionClick(selected[0], "No match, manually checked");
                    }
                }
            };
            commands.push(noMatchManuallyOkMenuItem);
        }
    } else if (selected && selected.length === 2) {
        if (
            (selected[0].__typename === "CorporateAction" && selected[1].__typename === "MiniTransaction") ||
            (selected[0].__typename === "MiniTransaction" && selected[1].__typename === "CorporateAction")
        ) {
            const corporateAction = selected[0].__typename === "CorporateAction" ? selected[0] : selected[1];
            const transaction = selected[0].__typename === "CorporateAction" ? selected[1] : selected[0];

            const matchTransactionCorporateAction = {
                key: "matchTransactionMenuItem",
                text: "Match",
                onClick: () => {
                    if (onMatchTransactionClick) {
                        onMatchTransactionClick(corporateAction, transaction._id);
                    }
                }
            };

            commands.push(matchTransactionCorporateAction);
        }
    }

    const onSelectedChange = (e) => {
        setSelected(e);
    };

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={
                        modal.payload.__typename === "MiniTransaction"
                            ? " Are you sure you want to delete selected transactions?"
                            : " Are you sure you want to delete selected corporate actions?"
                    }
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        if (onUpdateStatusClick) {
                            onUpdateStatusClick(modal.payload.ids, "Deleted");
                        }
                    }}
                />
            ) : null}
            <ReactDataGrid
                key={items}
                columns={columns}
                data={items}
                commands={selected && selected.length > 0 ? commands : []}
                defaultHiddenColumns={null}
                exportToXlsxFile={null}
                onSelectedChange={onSelectedChange}
            />
        </div>
    );
};

export const CorporateActionsGrid = (): React.ReactElement => {
    let relevantCorporateActions: CorporateActionExtended[] = [];
    const inputListDelete: CorporateActionInput[] = [];
    let unknownCorporateActions: CorporateActionExtended[] = [];
    let corporateActionsResulting: CorporateActionExtended[] = [];
    let transactionsCorporateActions: TransactionCorporateAction[] = [];

    const [{ fetching: loadingMe, error: errorMe, data }] = useQuery({
        query: GET_ME,
        requestPolicy: "network-only"
    });

    // All coacs in pending preliminary confirmed
    const filterAll = {
        statusIn: [
            CorporateActionStatusEnum.Pending,
            CorporateActionStatusEnum.Preliminary,
            CorporateActionStatusEnum.Confirmed,
            CorporateActionStatusEnum.Waiting
        ]
    };
    const [{ fetching: loading, error, data: allCorporateActions }, refetch] = useQuery({
        query: GET_CORPORATE_ACTIONS,
        variables: { filter: filterAll },
        requestPolicy: "network-only"
        //pollInterval: 15000
    });

    // messageType 564

    // corporateActionEvent: DRIP/DVOP, needs manual attention + 4:98A: <= todays date -> possibly preliminary confirmed
    const tomorrow = serializeSwedenDate(new Date().setDate(new Date().getDate() + 1));
    const today = serializeSwedenDate(new Date());
    const filterPreliminary = {
        statusIn: [
            CorporateActionStatusEnum.Pending,
            CorporateActionStatusEnum.Preliminary,
            CorporateActionStatusEnum.Confirmed,
            CorporateActionStatusEnum.Waiting
        ],
        corporateActionEventIn: [CorporateActionEvent.DRIP, CorporateActionEvent.DVOP],
        recordDateEnd: today,
        messageTypeIn: ["564"]
    };
    const [{ error: errorCorporateActionsPreliminary, data: dataCorporateActionsPreliminary }, refetchCorporateActionsPreliminary] =
        useQuery({
            query: GET_CORPORATE_ACTIONS,
            variables: { filter: filterPreliminary },
            requestPolicy: "network-only"
            //pollInterval: 15000
        });
    // corporateActionEvent: DRIP/DVOP, needs manual attention + 4:98A: > todays date -> waiting until included in recordDateEnd: today above
    const filterWait = {
        statusIn: [CorporateActionStatusEnum.Pending, CorporateActionStatusEnum.Preliminary, CorporateActionStatusEnum.Confirmed],
        corporateActionEventIn: [CorporateActionEvent.DRIP, CorporateActionEvent.DVOP],
        recordDateStart: tomorrow,
        messageTypeIn: ["564"]
    };
    const [{ error: errorCorporateActionsWait, data: dataCorporateActionsWait }, refetchCorporateActionsWait] = useQuery({
        query: GET_CORPORATE_ACTIONS,
        variables: { filter: filterWait },
        requestPolicy: "network-only"
        //pollInterval: 15000
    });
    // corporateActionEvent: DVCA/INTR/SHPR/CAPG/CAPD + 4:98A: <= todays date -> possibly confirm
    const filterConfirm = {
        statusIn: [CorporateActionStatusEnum.Pending, CorporateActionStatusEnum.Preliminary, CorporateActionStatusEnum.Confirmed],
        corporateActionEventIn: [
            CorporateActionEvent.DVCA,
            CorporateActionEvent.INTR,
            CorporateActionEvent.SHPR,
            CorporateActionEvent.CAPG,
            CorporateActionEvent.CAPD
        ],
        recordDateEnd: today,
        messageTypeIn: ["564"]
    };
    const [{ error: errorCorporateActionsConfirm, data: dataCorporateActionsConfirm }, refetchCorporateActionsConf] = useQuery({
        query: GET_CORPORATE_ACTIONS,
        variables: { filter: filterConfirm },
        requestPolicy: "network-only"
        //pollInterval: 5000
    });
    // corporateActionEvent: DVCA/INTR/SHPR/CAPG/CAPD/DRIP/DVOP + 4:98A: >todays date
    const filterDrop = {
        corporateActionEventIn: [
            CorporateActionEvent.DVCA,
            CorporateActionEvent.INTR,
            CorporateActionEvent.SHPR,
            CorporateActionEvent.CAPG,
            CorporateActionEvent.CAPD,
            CorporateActionEvent.DRIP,
            CorporateActionEvent.DVOP
        ],
        recordDateStart: tomorrow,
        messageTypeIn: ["564"]
    };
    const [{ error: errorCorporateActionsDrop, data: dataCorporateActionsDrop }, refetchCorporateActionsDrop] = useQuery({
        query: GET_CORPORATE_ACTIONS,
        variables: { filter: filterDrop },
        requestPolicy: "network-only"
        //pollInterval: 15000
    });

    // messageType 566, all updates
    const filterUpdate = {
        statusIn: [
            CorporateActionStatusEnum.Pending,
            CorporateActionStatusEnum.Preliminary,
            CorporateActionStatusEnum.Confirmed,
            CorporateActionStatusEnum.Waiting
        ],
        messageTypeIn: ["566"]
    };
    const [{ error: errorGetCorporateActionsUpdate, data: dataGetCorporateActionsUpdate }, refetchCorporateActionsUpdate] = useQuery({
        query: GET_CORPORATE_ACTIONS,
        variables: { filter: filterUpdate },
        requestPolicy: "network-only"
        //pollInterval: 15000
    });

    // For messageType 566, see if we have previous corporate action of type 564 and update that
    const [stateUpdate, updateCorporateActions] = useMutation(UPSERT_CORPORATE_ACTIONS);

    if (
        dataCorporateActionsConfirm &&
        dataGetCorporateActionsUpdate &&
        dataCorporateActionsPreliminary &&
        allCorporateActions &&
        dataCorporateActionsDrop
    ) {
        relevantCorporateActions = cloneDeep(
            dataCorporateActionsPreliminary.corporateActions.concat(
                dataCorporateActionsConfirm.corporateActions,
                dataGetCorporateActionsUpdate.corporateActions
            )
        ).filter((x: CorporateActionExtended) => x);

        const relevantCorporateActionIds: string[] = [];
        relevantCorporateActions.forEach((obj) => {
            if (obj) {
                obj.tradeDate = obj.exDate ? obj.exDate : null;
                obj.valueDate = obj.paymentDate ? obj.paymentDate : null;
                obj.clientName = obj.client ? obj.client.name : null;
                obj.instrumentName = obj.instrument ? obj.instrument.name : null;
                relevantCorporateActionIds.push(obj._id);
            }
        });
        const nonRelevantCorporateActionIds = [];

        dataCorporateActionsDrop.corporateActions.forEach(function (corporateAction: CorporateActionExtended) {
            //obj.tradeDate = obj.exDate ? obj.exDate : null;
            //obj.valueDate = obj.paymentDate ? obj.paymentDate : null;
            //obj.clientName = obj.client ? obj.client.name : null;
            //obj.instrumentName = obj.instrument ? obj.instrument.name : null;
            nonRelevantCorporateActionIds.push(corporateAction._id);
            const input = {
                _id: corporateAction._id,
                clientId: corporateAction.clientId,
                status: CorporateActionStatusEnum.Deleted
            };
            inputListDelete.push(input);
        });

        const knowHowToHandleCorporateActionIds = relevantCorporateActionIds.concat(nonRelevantCorporateActionIds);

        // Get the unknown pile of corporateActions, removing the ones we know we want to look at and the ones we know we do not want to look at
        const allCorporateActionsClone = cloneDeep(allCorporateActions.corporateActions);
        unknownCorporateActions = allCorporateActionsClone.filter(function (corporateAction: CorporateActionExtended) {
            corporateAction.tradeDate = corporateAction.exDate ? corporateAction.exDate : null;
            corporateAction.valueDate = corporateAction.paymentDate ? corporateAction.paymentDate : null;
            corporateAction.clientName = corporateAction.client ? corporateAction.client.name : null;
            corporateAction.instrumentName = corporateAction.instrument ? corporateAction.instrument.name : null;
            return !knowHowToHandleCorporateActionIds.includes(corporateAction._id);
        });

        corporateActionsResulting = relevantCorporateActions.concat(unknownCorporateActions);
    }

    const [{ fetching: loadingMiniTransactions, error: errorMiniTransactions, data: dataMiniTransactions }, refetchMiniTransactions] =
        useQuery({
            query: GET_MINI_TRANSACTIONS,
            variables: {
                status: [TransactionStatus.Pending, TransactionStatus.Preliminary, TransactionStatus.Confirmed],
                type: [TransactionType.Dividend, TransactionType.Interest]
            },
            requestPolicy: "network-only"
            //pollInterval: 15000
        });

    const [stateMatch, matchTransactions] = useMutation(MATCH_COACS_TRANSACTIONS);
    const [stateUpdateCorporateActions, updateCorporateActionsStatus] = useMutation(UPSERT_CORPORATE_ACTIONS);
    const [stateUpdateTransaction, updateTransactionStatus] = useMutation(UPDATE_TRANSACTIONS);

    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (errorMiniTransactions) return <p>error: {JSON.stringify(errorMiniTransactions, null, 2)}</p>;
    if (errorCorporateActionsPreliminary) return <p>error: {JSON.stringify(errorCorporateActionsPreliminary, null, 2)}</p>;
    if (errorCorporateActionsWait) return <p>error: {JSON.stringify(errorCorporateActionsWait, null, 2)}</p>;
    if (errorCorporateActionsConfirm) return <p>error: {JSON.stringify(errorCorporateActionsConfirm, null, 2)}</p>;
    if (errorCorporateActionsDrop) return <p>error: {JSON.stringify(errorCorporateActionsDrop, null, 2)}</p>;
    if (errorGetCorporateActionsUpdate) return <p>error: {JSON.stringify(errorGetCorporateActionsUpdate, null, 2)}</p>;
    if (stateUpdate.error) return <p>error: {JSON.stringify(stateUpdate.error, null, 2)}</p>;
    if (stateUpdateCorporateActions.error) return <p>error: {JSON.stringify(stateUpdateCorporateActions.error, null, 2)}</p>;
    if (stateUpdateTransaction.error) return <p>error: {JSON.stringify(stateUpdateTransaction.error, null, 2)}</p>;
    if (stateMatch.error) return <p>error: {JSON.stringify(errorMiniTransactions, null, 2)}</p>;
    if (errorMe)
        return (
            <div>
                <p>Error:</p>
                <pre> {JSON.stringify(errorMe, null, 2)}</pre>
            </div>
        );

    if (loading) return <p>Loading corporate actions</p>;
    if (loadingMiniTransactions) return <p>Loading transactions</p>;
    if (stateUpdate.fetching || stateMatch.fetching || loadingMe) return <p>Loading...</p>;

    // Filter transactions to show in pending list, keep relevant dividends and interests
    let extendedMiniTransactions: MiniTransactionExtended[];
    if (dataMiniTransactions.miniTransactions && corporateActionsResulting) {
        const miniTransactionsClone = cloneDeep(dataMiniTransactions.miniTransactions) as MiniTransactionExtended[];
        extendedMiniTransactions = miniTransactionsClone.filter(function (transaction) {
            //return (!t.error || t.error === "No matching corporate action transaction found.") && t.instrument.type === "Bond";
            transaction.clientName = transaction.client ? transaction.client.name : null;
            transaction.instrumentName = transaction.instrument ? transaction.instrument.name : null;
            return (
                transaction.instrument.modelType === InstrumentModelTypeEnum.Bond ||
                transaction.instrument.modelType === InstrumentModelTypeEnum.Stock
            );
        });

        // Filter transactions to show (not show both corporate action and transaction if matched)
        const filteredMiniTransactions = extendedMiniTransactions.filter(function (transaction) {
            return (
                (!transaction.error && transaction.status === TransactionStatus.Pending) ||
                (transaction.error === "No matching corporate action transaction found." &&
                    transaction.status === TransactionStatus.Pending)
            );
        });
        transactionsCorporateActions = [...corporateActionsResulting, ...filteredMiniTransactions];
    }

    // Update status for DRIP/DVOP with ex-date further ahead to waiting
    if (dataCorporateActionsWait && dataCorporateActionsWait.corporateActions && dataCorporateActionsWait.corporateActions.length) {
        const corporateActionsWaitingInput: CorporateActionInput[] = [];
        for (let s = 0; s < dataCorporateActionsWait.length; s++) {
            const corporateAction = cloneDeep(dataCorporateActionsWait[s]);
            corporateActionsWaitingInput.push({
                _id: corporateAction._id,
                status: CorporateActionStatusEnum.Waiting,
                clientId: corporateAction.clientId
            });
        }
        if (corporateActionsWaitingInput.length) {
            updateCorporateActionsStatus({
                input: corporateActionsWaitingInput
            })
                .then((result) => {
                    if (result.error) {
                        console.error(result.error.toString());
                    } else {
                        refetch();
                        refetchCorporateActionsPreliminary();
                        refetchCorporateActionsWait();
                        refetchCorporateActionsConf();
                        refetchCorporateActionsDrop();
                        refetchCorporateActionsUpdate();
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }

    const groups = groupByStatus(transactionsCorporateActions, transactionCoacsGroups);

    const updateStatus = async (items: TransactionCorporateAction[], changeTo: string) => {
        const updateTransactionsInput: UpdateTransactionInput[] = [];
        const updateCorporateActionsInput: CorporateActionInput[] = [];
        for (const item of items) {
            // Different mutations for corporateActions/transactions
            if (item.__typename === "CorporateAction") {
                updateCorporateActionsInput.push({ _id: item._id, status: changeTo as CorporateActionStatusEnum, clientId: item.clientId });
            } else {
                updateTransactionsInput.push({ _id: item._id, status: changeTo as TransactionStatus });
            }
        }
        if (updateTransactionsInput.length) {
            updateTransactionStatus({
                input: updateTransactionsInput
            }).then((result) => {
                if (result.error) {
                    console.error(result.error.toString());
                } else {
                    refetchMiniTransactions();
                }
            });
        }
        if (updateCorporateActionsInput.length) {
            updateCorporateActionsStatus({
                input: updateCorporateActionsInput
            })
                .then((result) => {
                    if (result.error) {
                        console.error(result.error.toString());
                    } else {
                        refetch();
                        refetchCorporateActionsPreliminary();
                        refetchCorporateActionsWait();
                        refetchCorporateActionsConf();
                        refetchCorporateActionsDrop();
                        refetchCorporateActionsUpdate();
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    };

    const doMatchTransactions = (item: CorporateActionExtended, changeTo: string) => {
        if (changeTo === "remove") {
            const matchTransaction: MatchCoacsTransactionsInput[] = [
                {
                    corporateActionId: item._id,
                    corporateActionError: null,
                    transactionId: item.correspondingTransactionId,
                    transactionError: null,
                    matchingStatus: MatchingCoacsStatus.Mismatch
                }
            ];
            matchTransactions({ input: matchTransaction })
                .then((result) => {
                    if (result.error) {
                        console.error(result.error.toString());
                    } else {
                        refetch();
                        refetchMiniTransactions();
                        refetchCorporateActionsPreliminary();
                        refetchCorporateActionsWait();
                        refetchCorporateActionsConf();
                        refetchCorporateActionsDrop();
                        refetchCorporateActionsUpdate();
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            console.log("matching coacs and transactions", matchTransaction);
        } else if (changeTo !== "No match, manually checked") {
            const matchTransaction: MatchCoacsTransactionsInput[] = [
                {
                    corporateActionId: item._id,
                    corporateActionError: "Manually matched",
                    transactionId: changeTo,
                    transactionError: "Manually matched",
                    matchingStatus: MatchingCoacsStatus.Confirmed
                }
            ];

            console.log(matchTransaction);
            matchTransactions({ input: matchTransaction }).then((result) => {
                if (result.error) {
                    console.error(result.error.toString());
                } else {
                    refetch();
                    refetchMiniTransactions();
                    refetchCorporateActionsPreliminary();
                    refetchCorporateActionsWait();
                    refetchCorporateActionsConf();
                    refetchCorporateActionsDrop();
                    refetchCorporateActionsUpdate();
                }
            });
            console.log("matching coacs and transactions", matchTransaction);
        } else {
            const updateCorporateActionInput: CorporateActionInput = {
                _id: item._id,
                clientId: item.clientId,
                status: CorporateActionStatusEnum.Settled,
                correspondingTransactionId: null,
                error: changeTo
            };

            console.log(updateCorporateActionInput);
            updateCorporateActions({ input: [updateCorporateActionInput] }).then((result) => {
                if (result.error) {
                    console.error(result.error.toString());
                } else {
                    refetch();
                    refetchCorporateActionsPreliminary();
                    refetchCorporateActionsWait();
                    refetchCorporateActionsConf();
                    refetchCorporateActionsDrop();
                    refetchCorporateActionsUpdate();
                }
            });
            console.log("updating corporate action", updateCorporateActionInput);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-4/12 px-2 mb-4">
                    <Button
                        variant="info"
                        type="submit"
                        style={{ marginLeft: "2px" }}
                        onClick={() => {
                            const win = window.open("/corporateactions/", "_blank");
                            win.focus();
                        }}
                    >
                        <TooltipLabel
                            text={"All corporate actions"}
                            content={"Page showing all coacs from a certain start date. All statuses are shown."}
                        />
                    </Button>
                </div>
                <div className="w-full md:w-8/12 px-2 mb-4 text-right">
                    {data &&
                    data.me &&
                    data.me.frontendRole &&
                    data.me.frontendRole.assets &&
                    (data.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) || data.me.frontendRole.assets.length === 0) ? (
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual/coac_management.md"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manual
                        </a>
                    ) : null}
                </div>
            </div>
            <br />
            <Fragment>
                {transactionCoacsGroups.map((status) => (
                    <div key={status} className="mt-4">
                        <h2>{status}</h2>
                        <CorporateActionsUpdateGrid
                            items={groups[status]}
                            onMatchTransactionClick={doMatchTransactions}
                            onUpdateStatusClick={updateStatus}
                        />
                    </div>
                ))}
            </Fragment>
        </div>
    );
};

export function CorporateActionsReconciliationPage(): React.ReactElement {
    const params = useParams<{ id: string; type: string }>();
    const [, navigate] = useLocation();
    const { id, type } = params;

    // Needed for corporate action form, possible transactions to match with
    const [exDateStart] = useQueryState("exDateStart", serializeSwedenDate(new Date(new Date().getFullYear(), 0, 1)));

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className={"w-full px-2 mb-4"}>
                    <CorporateActionsGrid />
                </div>
            </div>
            {id && type === "minitransaction" ? (
                <Panel isOpen={id != null} onDismiss={() => navigate(`/reconcile/corporateactions`)}>
                    <MiniTransactionForm id={id} type={null} showHeader />
                </Panel>
            ) : null}
            {id && type === "corporateaction" ? (
                <Panel isOpen={id != null} onDismiss={() => navigate(`/reconcile/corporateactions`)} title={"Corporate action"}>
                    <CorporateActionForm id={id} tradeDate={exDateStart} />
                </Panel>
            ) : null}
        </Fragment>
    );
}
